import React, { useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography, FormHelperText } from '@mui/material';
import Theme from '../../../../Themes/main.theme';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../../../components/Buttons/BackButton';
import TitleLayout from '../../../../components/layout/TitleLayout';
import InfoIcon from '@mui/icons-material/Info';
import { Stack } from '@mui/system';
import AccordionGeneral from '../../../../components/AccordionControlled/AccordionGeneral';
import SwitchButtonsList from '../../../../components/Buttons/SwitchButtonsList';
import FormActionButtonLayout from '../../../../components/layout/Form/FormActionButtonLayout';
import DialogAction from '../../../../components/Dialog/DialogAction';
import { external_roles_titles, internal_roles_titles, IRolePros } from './type';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DetailsWrapper from '../../../../components/Details/DetailsWrapper';

interface InputObject {
  name: string;
  [key: string]: any;
}

interface OutputObject {
  [key: string]: InputObject[];
}

function RoleForm(props: IRolePros) {
  const {title, internal, edit = false, view = false, initialValues, detailsRows, 
    permissionCategories, onSubmit, handleDelete} = props;

  const [roleTitle, setRoleTitle] = useState<string>(initialValues?.name);
  const [roleDescription, setRoleDescription] = useState<string>(initialValues?.description);
  const [permissionsOptions, setPermissionsOptions] = useState(permissionCategories);

  const [openCancel, setOpenCancel] = useState<boolean>(false);
  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);

  const [expanded, setExpanded] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();

  const handleAccordionChange = (accordion: string) => {
    const newExpanded = [...expanded];
    const isPanelExpanded = newExpanded.includes(accordion as never);
    isPanelExpanded
      ? newExpanded.splice(newExpanded.indexOf(accordion as never), 1)
      : newExpanded.push(accordion as never);
    setExpanded(newExpanded);
  };

  const isPanelExpanded = (accordion: string) => {
    return expanded.includes(accordion as never);
  };

  const handleExpandAll = () => {
    const allAccordions = permissionCategories.map((_: any, index:any) => `accordion${index + 1}`);
    setExpanded(allAccordions);
  };

  const handleCollapseAll = () => {
    setExpanded([]);
  };

  const handleSwitchButtonsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    options: any,
    setOptions: any
  ) => {
    const { name } = e.target;
    const findPermissionLocation = (target: any, arr: any) => {
      const groupRowArr = arr.map((item: any, rowIndex: any) => {
        const groupIdx = item.roles.findIndex((role: any) => role.name === target);
        return groupIdx !== -1
          ? {group: rowIndex, row: groupIdx}
          : null;
      });
      return groupRowArr.find((item: any) => item !== null) || null;
    };
    const {group, row} = findPermissionLocation(name, options);

    const newOption = JSON.parse(JSON.stringify(options));
    newOption[group].roles[row].checked=!newOption[group].roles[row].checked;
    setOptions(newOption);

  };
  
  const withState = (e: React.ChangeEvent<HTMLInputElement>, state: any, setState: any) => {
    return handleSwitchButtonsChange(e, state, setState);
  };
  
  const handlePermissionsOptions = (e: React.ChangeEvent<HTMLInputElement>) =>
    withState(e, permissionsOptions, setPermissionsOptions);

  const groupPermissionsByLabel = (array: InputObject[]): OutputObject => {
    const result: OutputObject = {};
    if (Array.isArray(array)) {
      for (const obj of array) {
        const names = obj.name.split(';');
        const name= names[0];
        if (!result[name]) {
          result[name] = [];
        }
        result[name].push(obj);
      }
    }
    return result;
  };

  const findCheckedRoles = (arr: any) => {
    const checkedIds = [];
    for (let i = 0; i < arr.length; i++) {
      const roles = arr[i].roles;
      for (let j = 0; j < roles.length; j++) {
        if (roles[j].checked) {
          checkedIds.push(roles[j]);
        }
      }
    }
    return checkedIds;
  };
  
  const arrayOfObj = (objOfObjs: any) =>
    Object.entries(objOfObjs).map((e) => ({ [e[0]]: e[1] }));

  React.useEffect(() => {
    setPermissionsOptions(permissionCategories);
  }, [permissionCategories]);
 
  React.useEffect(() => {
    setRoleDescription(roleDescription);
    setRoleTitle(roleTitle);
  }, [roleTitle, roleDescription]); 

  const disableButton = !roleTitle || !roleDescription || 
  findCheckedRoles(permissionsOptions).length ==0;

  return (
    <>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate('/admin/roles/dashboard')}/>
      </Box>
      <TitleLayout>
        <Stack flexDirection='row'>
          {!view && !edit
            ? <Typography variant='h2'>
              {internal
                ? 'Δημιουργία Εσωτερικού Ρόλου'
                : 'Δημιουργία Εξωτερικού Ρόλου'}
            </Typography>
            : <Typography variant='h2'> 
              {title}
            </Typography>}
          <Tooltip
            PopperProps={{
              disablePortal: true
            }} title={<Typography variant='subtitle2'>{internal
              ? 'Διατίθενται όλα τα ενδεχόμενα δικαιώματα εσωτερικού ρόλου. Μπορείτε να αφαιρέσετε ή να προσθέσετε δικαιώματα.'
              : 'Διατίθενται όλα τα ενδεχόμενα δικαιώματα εξωτερικού ρόλου. Μπορείτε να αφαιρέσετε ή να προσθέσετε δικαιώματα.'}
            </Typography>}
            placement='right-start'>
            <InfoIcon sx={{mr: 3, ml: 1, color: Theme?.iconColor?.main, fontSize: '25px'}}/>
          </Tooltip>
          {edit && 
          <Tooltip PopperProps={{
            disablePortal: true
          }} title={'Διαγραφή Ρόλου'}>
            <IconButton size='large'
              sx={{mt: -1}}
              disabled={
                internal_roles_titles?.includes(roleTitle) 
                || external_roles_titles?.includes(roleTitle)
                  ? true
                  : false 
              }
              onClick={() => setOpenDelete(true)}
            >
              <DeleteIcon 
                color='error'
                fontSize='large'/>
            </IconButton>
          </Tooltip>  
          }
          {view && 
          <Tooltip PopperProps={{
            disablePortal: true
          }} title={'Επεξεργασία Ρόλου'}>
            <IconButton size='large' sx={{mt: -1}}
              onClick={() => setOpenEdit(true)}>
              <EditIcon color='primary' fontSize='large'/>
            </IconButton>
          </Tooltip> 
          }
        </Stack>
        {!view ?
          <>
            <FormHelperText>
              <Typography
                variant='body1'
                textAlign='start'
                fontStyle='italic'
                sx={{
                  fontWeight: 'bolder',
                }}
              >
                Αφότου επεξεργαστείτε το πεδίο <u> Περιγραφή Ρόλου</u>, 
                η διαδικασία θα ολοκληρωθεί άμεσα,
                αλλά η αλλαγή στον συγκεντρωτικό πίνακα των ρόλων ενδέχεται 
                να διαρκέσει μερικά λεπτά.
              </Typography>
            </FormHelperText>
            <TextField label='Όνομα Ρόλου' variant='outlined'
              disabled={view}
              value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} 
            />
            <TextField
              value={roleDescription}
              onChange={(e) => setRoleDescription(e.target.value)}
              label='Περιγραφή Ρόλου'
              variant='outlined'
              disabled={view}
              multiline
              rows={4} />
          </>
          : <DetailsWrapper title={''} detailsProps={{
            rows: detailsRows, 
            removeLines: true, 
            renderArraysAsColumns: true }} />
        }
        <Box justifyContent='flex-end' display='flex'>
          <Button variant='contained' sx={{marginRight: 2}}
            onClick={handleExpandAll}>Εμφάνιση Όλων</Button>
          <Button variant='outlined' onClick={handleCollapseAll}>Απόκρυψη</Button>
        </Box>
        {permissionsOptions.map((permissionCategory: any, index: any) => {
          const acc = `accordion${index + 1}`;
          return (
            <>
              <AccordionGeneral
                accordionExpanded={isPanelExpanded(acc)}
                handleAccordionChange={() => handleAccordionChange(acc)}
                title={internal ? 
                  permissionCategory.name
                  : permissionCategory.name}>
                <SwitchButtonsList 
                  disabled={view}
                  handleSwitchChange={handlePermissionsOptions}
                  options={arrayOfObj(groupPermissionsByLabel(permissionCategory.roles))}
                />
              </AccordionGeneral>
            </>
          );
        })
        }
      </TitleLayout>
      <FormActionButtonLayout >
        {!view && <> 
          <Button onClick={() => setOpenCancel(true)}
            variant='outlined' color='error'> Ακύρωση </Button>
          <Button disabled={disableButton} onClick={() => setOpenSubmit(true)}
            variant='contained'>
            {edit
              ? 'Ολοκλήρωση Επεξεργασίας'
              : 'Δημιουργία Ρόλου'} 
          </Button>
        </>
        }
        <DialogAction open={openSubmit} handleClose={() => setOpenSubmit(false)}
          title={edit
            ?'Ολοκλήρωση Επεξεργασίας'
            :'Δημιουργία Ρόλου'} 
          content={
            edit
              ? internal
                ? 'Είστε σίγουρος/η ότι θέλετε να πραγματοποιήσετε τις αλλαγές στον Εσωτερικό Ρόλο' 
                : 'Είστε σίγουρος/η ότι θέλετε να πραγματοποιήσετε τις αλλαγές στον Εξωτερικό Ρόλο'
              : internal
                ? 'Είστε σίγουρος/η ότι θέλετε να δημιουργήσετε τον Εσωτερικό Ρόλο;'
                : 'Είστε σίγουρος/η ότι θέλετε να δημιουργήσετε τον Εξωτερικό Ρόλο;'
          }
          onAccept={() => {
            const checkedIds = findCheckedRoles(permissionsOptions);
            setOpenSubmit(false);
            onSubmit({
              id: id,
              name: roleTitle, 
              description: roleDescription, 
              roles: checkedIds
            });
          }}/>
        <DialogAction open={openCancel} handleClose={() => setOpenCancel(false)}
          title={edit
            ? 'Ακύρωση Επεξεργασίας'
            : 'Ακύρωση Δημιουργίας'} 
          content=
            {(edit && internal 
              ? 'Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε την επεξεργασία Εσωτερικού Ρόλου;'
              : 'Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε την επεξεργασία Εξωτερικού Ρόλου;'
            ) &&
            (internal
              ? 'Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε την δημιουργία Εσωτερικού Ρόλου;'
              : 'Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε την δημιουργία Εξωτερικού Ρόλου;')}
          onAccept={() => {
            setOpenCancel(false); navigate(-1);
          }}/>
        {edit &&
        <DialogAction open={openDelete} mode='delete'
          handleClose={() => setOpenDelete(false)}
          content={'Είστε σίγουρος ότι θέλετε να διαγράψετε τον Ρόλο;'}
          onAccept={() => {          
            handleDelete();
            setOpenDelete(false);
          }}
        />
        }
        {view &&
        <DialogAction open={openEdit} mode='edit'
          handleClose={() => setOpenEdit(false)}
          content={'Είστε σίγουρος ότι θέλετε να επεξεργαστείτε τον Ρόλο;'}
          onAccept={() => {
            navigate(internal
              ? `/admin/roles/edit-internal/${id}`
              : `/admin/roles/edit-external/${id}`);
            setOpenEdit(false);
          }}
        />
        }
      </FormActionButtonLayout>
    </>
  );
}

export default RoleForm;